import React, { useState, useEffect } from 'react'
import { supabase } from '../supabase'

export function Unsubscribe() {
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<'loading' | 'success' | 'error' | 'idle'>('idle')
  const [message, setMessage] = useState('')

  useEffect(() => {
    // Get email from URL parameters
    const urlParams = new URLSearchParams(window.location.search)
    const emailParam = urlParams.get('email')
    if (emailParam) {
      setEmail(emailParam)
    }
  }, [])

  const handleUnsubscribe = async (e: React.FormEvent) => {
    e.preventDefault()
    setStatus('loading')

    try {
      // Update the subscriber status in Supabase
      const { error } = await supabase
        .from('email_subscribers')
        .update({ 
          unsubscribed: true,
          unsubscribed_at: new Date().toISOString()
        })
        .eq('email', email)

      if (error) {
        throw error
      }

      // Call Netlify function to update Resend audience
      try {
        await fetch('/.netlify/functions/handle-unsubscribe', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email })
        })
      } catch (resendError) {
        console.error('Error updating Resend:', resendError)
        // Don't fail the whole process if Resend update fails
      }

      setStatus('success')
      setMessage('You have been successfully unsubscribed.')
    } catch (error) {
      console.error('Error:', error)
      setStatus('error')
      setMessage('Something went wrong. Please try again or contact team@nowpilot.ai')
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Unsubscribe</h1>
          <p className="text-gray-600">
            We're sorry to see you go. Enter your email below to unsubscribe from our mailing list.
          </p>
        </div>

        {status === 'success' ? (
          <div className="text-center">
            <div className="mb-4">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
              </div>
            </div>
            <p className="text-green-600 font-medium">{message}</p>
            <p className="text-gray-500 text-sm mt-4">
              Changed your mind? You can always subscribe again at{' '}
              <a href="/" className="text-[#4E69B5] hover:underline">nowpilot.ai</a>
            </p>
          </div>
        ) : (
          <form onSubmit={handleUnsubscribe}>
            <div className="mb-4">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4E69B5] focus:border-transparent"
                placeholder="your@email.com"
              />
            </div>

            {status === 'error' && (
              <p className="text-red-600 text-sm mb-4">{message}</p>
            )}

            <button
              type="submit"
              disabled={status === 'loading'}
              className="w-full bg-[#4E69B5] text-white py-2 px-4 rounded-lg hover:bg-[#3F5FAD] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {status === 'loading' ? 'Processing...' : 'Unsubscribe'}
            </button>

            <p className="text-center text-gray-500 text-sm mt-4">
              Having issues? Contact us at{' '}
              <a href="mailto:team@nowpilot.ai" className="text-[#4E69B5] hover:underline">
                team@nowpilot.ai
              </a>
            </p>
          </form>
        )}
      </div>
    </div>
  )
}